import { useEffect, useRef, useState } from 'react'

/**
 * FETCH BYTES → SHOW THEM → LET THEM GO. Written five times, identically.
 *
 * NoteAttachments, ProjectImage, AgentPanel's note image, and both of Attachment's
 * players each carried the same twenty lines: an `alive` flag, a `made` local, a
 * `.then` that mints an object URL, a `.catch`, and a teardown that revokes. Five
 * copies of a lifecycle where forgetting the last line is invisible — a leaked blob
 * costs the file's own size, silently, for as long as the page is open, and there is
 * no symptom until a long transcript has pinned a hundred of them.
 *
 * (App.tsx had a SIXTH version of the same mistake, found in M3: it minted a preview
 * URL inside the render with no revoke anywhere in the file, so every re-render leaked
 * another copy of the same staged photo.)
 *
 * WHY THE FETCHER LIVES IN A REF. Every one of those five carried an
 * `eslint-disable-next-line react-hooks/exhaustive-deps`, and they were right to: the
 * fetcher is a closure rebuilt on every render, so listing it would refetch forever.
 * The honest form is what this does — the effect keys ONLY on `key`, and reads the
 * latest fetcher from a ref at call time. The lint suppression disappears with the
 * duplication, which matters here because this repo has no linter to suppress
 * (see socketDeps.test.ts): a dep-array comment was the only record.
 *
 * `fetch` may be null to mean "not yet" — the audio and image players both wait for a
 * `ready` flag. A null does not clear an already-loaded url, because the two callers
 * that use it never go back to not-ready; if one ever does, that is the line to change.
 */
export function useBlobUrl(
  /** null = do not fetch yet. */
  fetch: (() => Promise<Blob>) | null,
  /** What identifies these bytes. The effect re-runs when any of it changes. */
  key: readonly unknown[],
): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  const fetchRef = useRef(fetch)
  fetchRef.current = fetch

  useEffect(() => {
    const run = fetchRef.current
    if (!run) return

    let alive = true
    let made: string | null = null
    setFailed(false)

    run()
      .then((blob) => {
        const u = URL.createObjectURL(blob)
        /*
         * REVOKED RIGHT HERE when the component is already gone, NOT left for the
         * teardown — the teardown has run. React tears down on unmount, which for an
         * in-flight fetch is BEFORE this resolves, so `made` was still null when the
         * cleanup read it and this URL was minted with nothing left to release it.
         *
         * All five copies this replaced had that hole, and it is the one that matters
         * most: closing a panel mid-load is the ordinary way to leave, and the bytes
         * that arrive afterwards are pinned for the life of the page with no symptom.
         * Found by a test written for the shared version — five identical copies had
         * never been asked the question once.
         */
        if (!alive) {
          URL.revokeObjectURL(u)
          return
        }
        made = u
        setUrl(u)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })

    return () => {
      alive = false
      if (made) URL.revokeObjectURL(made)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, key)

  return { url, failed }
}
